import {useEffect, useRef} from 'react';
import {NavigateOptions, To, useNavigate} from 'react-router';

let popstateListeners: Array<Function> = [];

window.addEventListener('popstate', () => {
  if (popstateListeners.length > 0) {
    // popstate 는 항상 마지막 것만 실행되게끔 함, 일회용임
    const listener = popstateListeners.pop() as Function;
    listener();
  }
});

function addPopstateListener(listener: Function) {
  popstateListeners.push(listener);
}

function removePopstateListener(listener: Function) {
  popstateListeners = popstateListeners.filter((l) => l !== listener);
}

function silenceHistoryBack(afterHistoryBack?: Function) {
  addPopstateListener(listener);
  window.history.back();

  /**
   * 가장 마지막 listener 만 실행되므로, 의미 없는 listener 하나 넣어서, 다음번의 history back 에 대한 전파는 없도록 함
   */
  function listener() {
    afterHistoryBack && afterHistoryBack();
    removePopstateListener(listener);
  }
}

/**
 * FIXME 이탈방지 및 모달 히스토리? 때 사용할 용도로 미리 인터페이스 만듬
 */
export function useCustomNavigate() {
  const navigate = useNavigate();

  function navigateTo(to: To, options?: NavigateOptions): void {
    navigate(to, options);
  }

  function navigateBack() {
    navigate(-1);
  }

  return {
    navigateTo,
    navigateBack,
  };
}

type CloseFunction<T extends Array<any>> = (...args: T) => void;

export function useModalBackNavigate<T extends Array<any>>(close: CloseFunction<T>, whenHistoryBack?: () => T[0]) {
  const isInitRef = useRef<boolean>(false);
  const paramsRef = useRef<T | undefined>();

  function navigateClose(...values: T) {
    paramsRef.current = values;
    window.history.back();
  }

  useEffect(() => {
    if (!isInitRef.current) {
      isInitRef.current = true;
      window.history.pushState(null, '', window.location.href);
    }
    addPopstateListener(backToCloseListener);

    function backToCloseListener() {
      if (paramsRef.current === undefined) {
        // @ts-ignore
        close(whenHistoryBack ? whenHistoryBack() : false);
      } else {
        close(...paramsRef.current);
      }
    }

    return () => {
      removePopstateListener(backToCloseListener);
    };
  }, [close, whenHistoryBack]);

  return {navigateClose};
}

export function useEscapeBlock(onBlocked: () => Promise<boolean>) {
  useEffect(() => {
    startBlocking();

    window.onbeforeunload = (event) => {
      const message = ''; // 브라우저에서 어차피 동작 안함
      event.returnValue = message;
      return message;
    };

    async function escapeBlockListener() {
      const isConfirm = await onBlocked();
      if (isConfirm) {
        window.history.back();
      } else {
        startBlocking();
      }
    }

    function startBlocking() {
      window.history.pushState(null, '', window.location.href);
      addPopstateListener(escapeBlockListener);
    }

    return () => {
      removePopstateListener(escapeBlockListener);
      window.onbeforeunload = null;
    };
  }, []);
}
